import { useEffect, useState } from 'react'

declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          AutocompleteService: new () => google.maps.places.AutocompleteService
          AutocompleteSessionToken: new () => google.maps.places.AutocompleteSessionToken
        }
      }
    }
    __googleMapsLoading?: Promise<void>
    __googleMapsReady?: () => void
  }
}

interface UseGoogleMapsReturn {
  isLoaded: boolean
  error: string | null
}

// Singleton: ensure the script is only injected once
// Uses `callback` param so Google fires __googleMapsReady only after full initialization
function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (window.google?.maps?.places) {
    return Promise.resolve()
  }
  if (window.__googleMapsLoading) {
    return window.__googleMapsLoading
  }
  window.__googleMapsLoading = new Promise<void>((resolve, reject) => {
    window.__googleMapsReady = resolve
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&region=TW&language=zh-TW&callback=__googleMapsReady`
    script.async = true
    script.defer = true
    script.onerror = () => reject(new Error('Failed to load Google Maps API'))
    document.head.appendChild(script)
  })
  return window.__googleMapsLoading
}

export const useGoogleMaps = (): UseGoogleMapsReturn => {
  const [isLoaded, setIsLoaded] = useState(() => !!window.google?.maps?.places)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isLoaded) return

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    if (!apiKey) {
      setError('Google Maps API key not configured')
      return
    }

    loadGoogleMapsScript(apiKey)
      .then(() => setIsLoaded(true))
      .catch(() => setError('Failed to load Google Maps API'))
  }, [isLoaded])

  return { isLoaded, error }
}
